<template>
  <FormContainer>
    <template #title>L'emploi visé par la PRO-A :</template>
    <template #content>
      <div class="form__bloc">
        <WrapperDropdown
          id="field_73"
          v-model="proaViseModel"
          label="La Pro-A vise"
          :required="meta.contrat?.proaVise?.$status === PropertyStatus.Required"
          :alertLabel="meta.contrat?.proaVise?.$messageErreurDirty!"
          class="col-12 col-md-18 col-sm-36"
          :items="lov.ProaVise"
          key-id="code"
          key-label="intitule"
          :readonly
        />
        <WrapperInput
          id="field_75"
          v-model="emploiViseModel"
          label="Emploi visé par la Pro-A (intitulé précis)"
          :required="meta.contrat?.emploiVise?.$status === PropertyStatus.Required"
          :alertLabel="meta.contrat?.emploiVise?.$messageErreurDirty!"
          :maxlength="200"
          deletable
          :readonly
          class="col-24 col-md-18 col-sm-36"
        />
      </div>
      <div class="form__bloc">
        <BasicCheckbox
          id="field_74"
          v-model="acquisVaeModel"
          label="La PRO-A comporte-t-elle des actions permettant de valider les acquis de l’expérience (VAE) ?"
          :required="meta.contrat?.acquisVae?.$status === PropertyStatus.Required"
          :alert-message="meta.contrat?.acquisVae?.$messageErreurDirty!"
          class="col-36"
          :readonly
        />
      </div>
      <div class="form__bloc">
        <WrapperInput
          id="field_77"
          v-model="emploinCcnModel"
          label="Classification de l'emploi dans la convention collective"
          :required="meta.contrat?.emploiCcnVise?.$status === PropertyStatus.Required"
          :alertLabel="meta.contrat?.emploiCcnVise?.$messageErreurDirty!"
          deletable
          :readonly
          class="col-24 col-sm-36"
        />
      </div>
      <div class="form__bloc">
        <BasicCheckbox
          id="field_76"
          v-model="effectueeSurLeTempsDeTravailModel"
          label="La PRO-A est effectuée sur le temps de travail ?"
          :required="meta.contrat?.effectueeSurLeTempsDeTravail?.$status === PropertyStatus.Required"
          :alert-message="meta.contrat?.effectueeSurLeTempsDeTravail?.$messageErreurDirty!"
          class="col-36 col-sm-36"
          :readonly
        />
      </div>
      <div class="form__bloc">
        <WrapperInput
          id="field_78"
          v-model="niveauQualificationCcnViseModel"
          label="Niveau de l'emploi"
          :required="meta.contrat?.niveauQualificationCcnVise?.$status === PropertyStatus.Required"
          :alertLabel="meta.contrat?.niveauQualificationCcnVise?.$messageErreurDirty!"
          class="col-12 col-sm-36"
          :readonly
          :maxlength="15"
        />
        <WrapperInput
          id="field_79"
          v-model="coefficientCcnViseModel"
          label="Coefficient hiérarchique"
          :required="meta.contrat?.coefficientCcnVise?.$status === PropertyStatus.Required"
          :alertLabel="meta.contrat?.coefficientCcnVise?.$messageErreurDirty!"
          :maxlength="5"
          deletable
          :readonly
          class="col-12 col-sm-36"
        />
        <WrapperInputDate
          id="field_80"
          v-model="dateDebutExecutionProAModel"
          label="Date de début d’exécution de la Pro-A"
          :required="meta.contrat?.dateDebutExecutionProA?.$status === PropertyStatus.Required"
          :alertLabel="meta.contrat?.dateDebutExecutionProA?.$messageErreurDirty!"
          :hint="HINT_DATE"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_DATE"
          deletable
          :readonly
        />
      </div>
      <div class="form__bloc">
        <WrapperInputDate
          id="field_81"
          v-model="dateFinExecutionProAModel"
          label="Date de fin d’exécution de la Pro-A"
          :required="meta.contrat?.dateFinExecutionProA?.$status === PropertyStatus.Required"
          :alertLabel="meta.contrat?.dateFinExecutionProA?.$messageErreurDirty!"
          :hint="HINT_DATE"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_DATE"
          deletable
          :readonly
        />
        <WrapperInputDuree
          id="field_82"
          v-model="dureeHebdomadaireModel"
          label="Durée hebdomadaire du travail"
          :required="meta.contrat?.dureeHebdomadaire?.heure?.$status === PropertyStatus.Required"
          :alertLabel="meta.contrat?.dureeHebdomadaire?.heure?.$messageErreurDirty!"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_DUREE"
          :hint="HINT_DUREE"
          deletable
          :maxlength="5"
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import type { DureeHebdomadaireDTO } from '@/features/api/extranet/dto/Demande/shared'
import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
import {
  FormContainer,
  HINT_DATE,
  HINT_DUREE,
  PLACEHOLDER_DATE,
  PLACEHOLDER_DUREE,
} from '@/features/interface/demande/shared'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import { useDemandePROAStore } from '../../../../useDemandePROAStore'

  const store = useDemandePROAStore()
  const { setupFieldWithKey } = store
  const { meta, lov, readonly } = storeToRefs(store)

  const proaViseModel = setupFieldWithKey<string>('contrat.proaVise', false)
  const acquisVaeModel = setupFieldWithKey<boolean>('contrat.acquisVae', false)
  const emploiViseModel = setupFieldWithKey<string>('contrat.emploiVise', false)
  const effectueeSurLeTempsDeTravailModel = setupFieldWithKey<boolean>('contrat.effectueeSurLeTempsDeTravail', false)
  const emploinCcnModel = setupFieldWithKey<string>('contrat.emploiCcnVise', true)
  const niveauQualificationCcnViseModel = setupFieldWithKey<string>('contrat.niveauQualificationCcnVise', false)
  const coefficientCcnViseModel = setupFieldWithKey<string>('contrat.coefficientCcnVise', true)
  const dateDebutExecutionProAModel = setupFieldWithKey<string>('contrat.dateDebutExecutionProA', true)
  const dateFinExecutionProAModel = setupFieldWithKey<string>('contrat.dateFinExecutionProA', true)
  const dureeHebdomadaireModel = setupFieldWithKey<DureeHebdomadaireDTO>('contrat.dureeHebdomadaire', true)
  // Initialise acquisVae à false si null pour que le CERFA coche "Non" par défaut
  watch(acquisVaeModel, (value) => {
    if (value === null || value === undefined) {
      acquisVaeModel.value = false
    }
  }, { immediate: true })
</script>
